import { Dispatch, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import Field from '../../components/shared/Field/Field';
import appState from '../../state/AppStateContainer';
import { EvaluationLimits, EvaluationQuestionType } from '../../util/evaluationContract';
import { buildNameList } from '../../util/supplierBulkOutcome';
import { formatShortDay } from '../page-supplier-evaluations/reviewFormat';
import {
  WizardAction,
  WizardData,
  WizardState,
  effectiveTagIds,
  mailCount,
  templateOf,
  titleValue,
} from './wizardState';
import styles from './app-evaluation-wizard.module.scss';

type Props = {
  state: WizardState;
  data: WizardData;
  dispatch: Dispatch<WizardAction>;
};

const MAX_NAMED_SUPPLIERS = 5;

const questionsText = (t: TFunction, count: number) =>
  t('wizard.template.count', {
    count,
    defaultValue_one: '{{count}} fråga',
    defaultValue_other: '{{count}} frågor',
  });

const projectText = (t: TFunction, state: WizardState) => {
  if (state.contextChoice === 'project' && state.project) return state.project.name;
  if (state.contextChoice === 'historical') {
    const name = state.historicalName.trim();
    const number = state.historicalNumber.trim();
    return name && number
      ? t('wizard.review.historical', '{{name}} · {{number}} (historiskt)', { name, number })
      : t('overview.supplier.historical', '{{name}} (historiskt)', { name: name || number });
  }
  return t('wizard.context.none', 'Inget projekt');
};

const templateText = (t: TFunction, state: WizardState, data: WizardData) => {
  const template = templateOf(state, data);
  if (template) {
    const extras = state.extraQuestions.length;
    const removed = template.questions.filter(
      question =>
        question.type !== EvaluationQuestionType.Paragraph &&
        state.removedQuestionKeys.includes(question.key),
    ).length;
    const questions = questionsText(t, template.questionCount - removed + extras);
    const extrasText = t('wizard.review.extras', {
      count: extras,
      defaultValue_one: '+ {{count}} egen fråga',
      defaultValue_other: '+ {{count}} egna frågor',
    });
    if (removed) {
      const removedText = t('wizard.review.removed', {
        count: removed,
        defaultValue_one: '{{count}} borttagen',
        defaultValue_other: '{{count}} borttagna',
      });
      return extras
        ? t(
            'wizard.review.templateRemovedExtras',
            '{{name}}, {{removed}} {{extras}} ({{questions}} totalt)',
            { name: template.name, removed: removedText, extras: extrasText, questions },
          )
        : t('wizard.review.templateRemoved', '{{name}}, {{removed}} ({{questions}} totalt)', {
            name: template.name,
            removed: removedText,
            questions,
          });
    }
    return extras
      ? t('wizard.review.templateExtras', '{{name}} {{extras}} ({{questions}} totalt)', {
          name: template.name,
          questions,
          extras: extrasText,
        })
      : t('wizard.review.templateOnly', '{{name}} · {{questions}}', {
          name: template.name,
          questions,
        });
  }
  // The server ends an Egna frågor utvärdering with Övrigt, which the respondent also answers
  return t('wizard.review.ownOnly', 'Egna frågor · {{questions}}', {
    questions: questionsText(t, state.ownQuestionIds.length + 1),
  });
};

// The tag list can fail to load while the send still carries the handed-over tags
const tagsText = (t: TFunction, count: number, names: string[]) => {
  if (!count) return t('wizard.review.noTags', 'Inga taggar');
  if (names.length === count) return names.join(', ');
  return t('wizard.review.tagCount', {
    count,
    defaultValue_one: '{{count}} tagg',
    defaultValue_other: '{{count}} taggar',
  });
};

const ReviewStep = ({ state, data, dispatch }: Props) => {
  const [t, i18n] = useTranslation('evaluations');
  const several = state.suppliers.length > 1;
  const title = titleValue(t, state);
  const mails = mailCount(state, { userId: appState.getUserId(), email: appState.getUserEmail() });
  const tagIds = effectiveTagIds(state, data);
  const tagNames = tagIds
    .map(id => data.tags.find(tag => tag._id === id)?.name)
    .filter((name): name is string => Boolean(name));
  const suppliers = buildNameList(
    state.suppliers.map(supplier => supplier._id),
    id => state.suppliers.find(supplier => supplier._id === id)?.name,
    MAX_NAMED_SUPPLIERS,
  );

  const titleHelp = () => {
    if (!several) return t('wizard.review.titleHelp', 'Visas i listan över utvärderingar.');
    if (!title.trim()) {
      return t(
        'wizard.review.titleSeveral',
        'Lämna tomt så får varje utvärdering leverantörens namn.',
      );
    }
    return t('wizard.review.titleAll', {
      count: state.suppliers.length,
      defaultValue_one:
        'Titeln gäller {{count}} utvärdering. Töm fältet så får den leverantörens namn.',
      defaultValue_other:
        'Titeln gäller alla {{count}} utvärderingar. Töm fältet så får var och en leverantörens namn.',
    });
  };

  const mailText = t('wizard.review.mailCount', {
    count: mails.mails,
    defaultValue_one: '{{count}} mejl',
    defaultValue_other: '{{count}} mejl',
  });

  const rows: [string, string][] = [
    [t('wizard.review.project', 'Projekt'), projectText(t, state)],
    [t('wizard.review.tags', 'Taggar'), tagsText(t, tagIds.length, tagNames)],
    [
      t('wizard.review.suppliers', 'Leverantörer'),
      suppliers.more
        ? `${suppliers.shown.join(', ')} ${t('toast.andMore', 'och {{more}} till', {
            more: suppliers.more,
          })}`
        : suppliers.shown.join(', '),
    ],
    [t('wizard.review.template', 'Mall'), templateText(t, state, data)],
    [
      t('wizard.review.recipients', 'Mottagare'),
      t('wizard.review.people', {
        count: state.recipients.length,
        defaultValue_one: '{{count}} person',
        defaultValue_other: '{{count}} personer',
      }),
    ],
    [
      t('wizard.review.mails', 'Mejl'),
      several
        ? t('wizard.review.mailsBreakdown', '{{mails}} ({{suppliers}} × {{people}})', {
            mails: mailText,
            suppliers: t('wizard.review.supplierCount', {
              count: mails.suppliers,
              defaultValue_one: '{{count}} leverantör',
              defaultValue_other: '{{count}} leverantörer',
            }),
            people: t('wizard.review.people', {
              count: mails.people,
              defaultValue_one: '{{count}} person',
              defaultValue_other: '{{count}} personer',
            }),
          })
        : mailText,
    ],
    [t('wizard.review.due', 'Sista svarsdag'), formatShortDay(state.dueDay, i18n.language)],
  ];

  return (
    <div className={styles.fields}>
      <div className={styles.fieldStack}>
        <Field
          label={t('quick.titleLabel', 'Titel')}
          value={title}
          onChange={text =>
            dispatch({ type: 'setTitle', title: text.slice(0, EvaluationLimits.TitleMaxLength) })
          }
        />
        <p className={styles.hint}>{titleHelp()}</p>
      </div>
      <dl className={styles.summary}>
        {rows.map(([label, value]) => (
          <Fragment key={label}>
            <dt>{label}</dt>
            <dd>{value}</dd>
          </Fragment>
        ))}
      </dl>
      {mails.selfIncluded && (
        <p className={styles.hint}>
          {t('wizard.review.selfNoMail', 'Du får inget mejl för din egen rad.')}
        </p>
      )}
    </div>
  );
};

export default ReviewStep;
